import { Editor, KeyboardShortcutCommand, isNodeActive, findParentNode, isAtEndOfNode } from '@tiptap/core';
import { ListKeymap as ListKeymapExtension, listHelpers } from '@tiptap/extension-list-keymap';
import { EditorState } from '@tiptap/pm/state';
import {
  CustomBulletListExtensionName,
  CustomListItemExtensionName,
  CustomOrderedListExtensionName,
} from './utils/names';

export const ListKeymap = ListKeymapExtension.extend({
  addKeyboardShortcuts() {
    const parentKeyboardShortcuts = this.parent?.() ?? {};

    const customKeyboardShortcuts: Record<string, KeyboardShortcutCommand> = {
      // Changing internals
      Delete: ({ editor }) => {
        let handled = false;

        this.options.listTypes.forEach(({ itemName }) => {
          if (editor.state.schema.nodes[itemName] === undefined) {
            return;
          }

          if (handleDelete(editor, itemName)) {
            handled = true;
          }
        });

        return handled;
      },
      'Mod-Delete': ({ editor }) => {
        let handled = false;

        this.options.listTypes.forEach(({ itemName }) => {
          if (editor.state.schema.nodes[itemName] === undefined) {
            return;
          }

          if (handleDelete(editor, itemName)) {
            handled = true;
          }
        });

        return handled;
      },
    };

    return {
      ...parentKeyboardShortcuts,
      ...customKeyboardShortcuts,
    };
  },
}).configure({
  listTypes: [
    {
      itemName: CustomListItemExtensionName,
      wrapperNames: [CustomBulletListExtensionName, CustomOrderedListExtensionName],
    },
  ],
});

function isAtEndOfFirstChildNode(state: EditorState, nodeType?: string) {
  const { $from, $to } = state.selection;

  // Ignore if the $from and $to position are not the same, as well as the nodeType not being available
  if ($from.pos === $to.pos && nodeType) {
    const parentNode = findParentNode((node) => node.type.name === nodeType)(state.selection);
    if (!parentNode) {
      return false;
    }

    const $parentPos = state.doc.resolve(parentNode.pos + 1);

    // Check if the position is at the end of the first child node
    if ($from.pos - ($parentPos.start() + 1) === parentNode.node.content.firstChild?.content.size) {
      return true;
    }

    return false;
  }
}

/**
 * Most of this code is taken from the original listKeymap tiptap extension, we are just trying to catch an edge case it doesn't cover.
 * @see https://github.com/ueberdosis/tiptap/blob/main/packages/extension-list-keymap/src/listHelpers/handleDelete.ts
 */
export const handleDelete = (editor: Editor, name: string) => {
  // if the cursor is not inside the current node type
  // do nothing and proceed
  if (!isNodeActive(editor.state, name)) {
    return false;
  }

  // if the cursor is not at the end of a node
  // do nothing and proceed
  if (!isAtEndOfFirstChildNode(editor.state, name) && !isAtEndOfNode(editor.state, name)) {
    return false;
  }

  // if the selection is not collapsed, or not within a single node
  // do nothing and proceed
  const { selection } = editor.state;
  const { $from, $to } = selection;

  if (!selection.empty && $from.sameParent($to)) {
    return false;
  }

  // check if the next node is a list with a deeper depth
  if (listHelpers.nextListIsDeeper(name, editor.state)) {
    return editor
      .chain()
      .focus(editor.state.selection.from + 4)
      .liftListItem(name)
      .joinItemBackward()
      .run();
  }

  if (listHelpers.nextListIsHigher(name, editor.state)) {
    return editor.chain().joinForward().joinBackward().run();
  }

  return editor.commands.joinItemForward();
};
